import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const subscriptionId = searchParams.get('provider') === 'paypal' ? searchParams.get('subscription_id') : null;
  const orderId = searchParams.get('provider') === 'paypal_order' ? searchParams.get('token') : null;
  const sessionId = searchParams.get('session_id') || subscriptionId || orderId;
  const [state, setState] = useState({ stage: 'polling', message: 'Verifying your payment...' });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ stage: 'error', message: 'No payment session was found.' });
      return;
    }

    let cancelled = false;
    const maxAttempts = subscriptionId ? 20 : 8;
    const interval = 2000;

    const poll = async () => {
      if (cancelled) return;
      try {
        const url = subscriptionId
          ? `${API}/paypal/subscription-status/${subscriptionId}`
          : `${API}/checkout/status/${sessionId}`;
        const { data } = orderId
          ? await axios.post(`${API}/paypal/capture/${orderId}`)
          : await axios.get(url);
        if (data.payment_status === 'paid') {
          setState({
            stage: 'success',
            message: subscriptionId
              ? 'Thank you! Your monthly plan is set up. Each remaining payment is charged automatically through PayPal and billing stops after the final one. Chanah will reach out via WhatsApp shortly to schedule your first session.'
              : "Thank you! Your application and payment are received. Chanah will reach out via WhatsApp shortly to schedule your first session.",
          });
          return;
        }
        if (data.status === 'expired' || data.payment_status === 'cancelled') {
          setState({ stage: 'error', message: 'Payment session expired. Please try again.' });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= maxAttempts) {
          setState({
            stage: 'pending',
            message: "We're still verifying your payment. You'll receive a confirmation by email/WhatsApp once it clears.",
          });
          return;
        }
        setTimeout(poll, interval);
      } catch (e) {
        setState({ stage: 'error', message: 'There was an error verifying your payment. Please contact us via WhatsApp.' });
      }
    };
    poll();

    return () => { cancelled = true; };
  }, [sessionId, subscriptionId, orderId]);

  return (
    <div className="payment-result-page" data-testid="payment-success-page">
      <div className="payment-result-card">
        {state.stage === 'polling' && (
          <>
            <div className="spinner" data-testid="payment-spinner" />
            <h1 className="heading-1">Verifying Payment</h1>
            <p className="body-medium">{state.message}</p>
          </>
        )}
        {state.stage === 'success' && (
          <>
            <div className="payment-result-icon success">
              <CheckCircle size={40} />
            </div>
            <h1 className="heading-1" data-testid="payment-success-title">Payment Received!</h1>
            <p className="body-medium">{state.message}</p>
            <Link to="/" className="btn-primary" data-testid="payment-back-home">
              Return Home
            </Link>
          </>
        )}
        {state.stage === 'pending' && (
          <>
            <div className="payment-result-icon">
              <Loader size={40} />
            </div>
            <h1 className="heading-1">Almost there</h1>
            <p className="body-medium">{state.message}</p>
            <Link to="/" className="btn-primary">Return Home</Link>
          </>
        )}
        {state.stage === 'error' && (
          <>
            <div className="payment-result-icon error">
              <AlertCircle size={40} />
            </div>
            <h1 className="heading-1" data-testid="payment-error-title">Something went wrong</h1>
            <p className="body-medium">{state.message}</p>
            <Link to="/" className="btn-primary">Return Home</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
